import { AxiosError } from "axios"
import { axiosPSClient } from "@/api/PSApiOperations"
import { Sentry } from "@/telemetry/sentry"
import { getTelemetryRequestPath } from "@/telemetry/request-path"

// -- Error classifiers

const isAccountMigratedError = (error: AxiosError): boolean => {
	if (error.response?.status !== 403) return false
	const detail = (error.response.data as { detail?: unknown } | undefined)?.detail
	return (
		!!detail &&
		typeof detail === "object" &&
		"reason" in detail &&
		detail.reason === "account_migrated"
	)
}

const isNotAuthenticatedError = (error: AxiosError): boolean => {
	if (error.response?.status !== 403) return false
	const detail = (error.response.data as { detail?: unknown } | undefined)?.detail
	return detail === "Not authenticated"
}

// -- Error handler factories

export const createAxiosClientResponseErrorHandler =
	({
		onLoginRequired,
		onAccountMigrated,
	}: {
		onLoginRequired: () => void
		onAccountMigrated: () => void
	}) =>
	(error: unknown): Promise<never> => {
		if (!(error instanceof AxiosError)) {
			return Promise.reject(error)
		}

		if (isAccountMigratedError(error)) {
			onAccountMigrated()
		}

		if (error.response?.status === 401 || isNotAuthenticatedError(error)) {
			onLoginRequired()
		}

		return Promise.reject(error)
	}

export const createAxiosPSClientResponseErrorHandler =
	({ onLoginRequired }: { onLoginRequired: () => void }) =>
	(error: unknown): Promise<never> => {
		if (!(error instanceof AxiosError)) {
			return Promise.reject(error)
		}

		const status = error.response?.status

		if (status === 419) {
			const currentWorkspaceId = axiosPSClient.defaults.headers.common["X-PS-WS-ID"]
			const requestWorkspaceId = error.config?.headers?.["X-PS-WS-ID"]

			Sentry.captureException(error, {
				captureContext: {
					extra: {
						url: getTelemetryRequestPath(error.config?.url),
						method: error.config?.method,
						status,
						hasCurrentWorkspace: Boolean(currentWorkspaceId),
						hasRequestWorkspace: Boolean(requestWorkspaceId),
						workspaceMismatch: currentWorkspaceId !== requestWorkspaceId,
					},
				},
			})
		}

		if (status === 401) {
			onLoginRequired()
		}

		return Promise.reject(error)
	}
